import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { asLanguage, isLanguage, type Language } from '@/modules/i18n/languages';

interface LanguagePath {
    lang: Language | undefined;
    // The page path under the language, from its leading slash: `/` for the home page
    rest: string;
}

// `/ru/cookbook` → ru + `/cookbook`. A first segment that is not a language is part of the page path:
// `/cookbook` and `/de/cookbook` carry no language
export const parseLanguagePath = (pathname: string): LanguagePath => {
    const match = /^\/([^/]+)(\/.*)?$/.exec(pathname);
    const segment = match?.[1];
    return isLanguage(segment)
        ? { lang: segment, rest: match?.[2] ?? '/' }
        : { lang: undefined, rest: pathname };
};

export const buildLanguagePath = (lang: Language, rest: string): string => `/${lang}${rest}`;

/**
 * The language of the current URL. Read from useLocation(), not useParams(): it works outside the matched routes too.
 * A URL without one only renders before LanguageGate replaces it; it stands at i18next's language meanwhile.
 */
export const useLanguage = (): Language => {
    const { pathname } = useLocation();
    const { i18n } = useTranslation();
    return parseLanguagePath(pathname).lang ?? asLanguage(i18n.resolvedLanguage);
};

// Internal links in the current language: `localize('/cookbook')` → `/ru/cookbook`
export const useLocalizedPath = (): ((path: string) => string) => {
    const lang = useLanguage();
    return useCallback((path: string) => buildLanguagePath(lang, path), [lang]);
};
